import { Children, cloneElement, createContext, useContext, useEffect, useMemo, useRef, useState } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { formatApiErrorDetail } from "../../api";
import { OWNER_LABEL, OWNER_OPTIONS, checkAnchorId } from "./labels";
import { readMinutes } from "./search";

// The checks are written in the card body as bold leads —
// `**H1 — Money stays private.**`. Anchoring those gives the drawer's approve
// checklist somewhere to link each check to. Derived from the text rather than
// stored, because the body is Marco-editable: an edit that drops the notation
// costs a scroll position, not a broken page.
const CHECK_LEAD = /^([HCS]\d{1,2})\s*[—–-]/;

function checkText(children) {
  if (typeof children === "string") return children;
  if (Array.isArray(children))
    return children.map((c) => (typeof c === "string" ? c : "")).join("");
  return "";
}

function MdStrong({ children }) {
  const m = CHECK_LEAD.exec(checkText(children).trim());
  return m ? <strong id={checkAnchorId(m[1])}>{children}</strong> : <strong>{children}</strong>;
}

// Playbook bodies use the pseudo-link (#capture) to send Ward straight to the
// capture form. Everything else is a normal external link.
function mdLink(onOpenCapture) {
  return function MdLink({ href, children }) {
    if (href === "#capture") {
      return (
        <button type="button" className="mk-inline-link" onClick={onOpenCapture}
          data-testid="mk-open-capture">
          {children}
        </button>
      );
    }
    return <a href={href} target="_blank" rel="noopener noreferrer">{children}</a>;
  };
}

// A table on a phone is a horizontal scroll inside a vertical one, and the
// tables here — the channel table, the shot list, the pilot rotation — are the
// most-referenced content in the section. Below 640px the CSS turns each row
// into its own block and hides the header row, which only works if every cell
// carries the column name it lost. That is what `data-label` is for; on a wide
// screen it is inert and the table renders as a table.
const HeaderContext = createContext([]);

// react-markdown renders intrinsic elements, so `type` is the tag name.
function textOf(node) {
  if (node == null || typeof node === "boolean") return "";
  if (typeof node === "string" || typeof node === "number") return String(node);
  if (Array.isArray(node)) return node.map(textOf).join("");
  if (node.props) return textOf(node.props.children);
  return "";
}

function headersOf(children) {
  const thead = Children.toArray(children).find((c) => c.type === "thead");
  if (!thead) return [];
  // Not `.find(r => r.type === "tr")`: the header row goes through MdTr like
  // any other, so its element type is that component, not the tag name. Take
  // the first element child instead of matching on the type at all.
  const row = Children.toArray(thead.props.children).find((r) => r?.props);
  if (!row) return [];
  return Children.toArray(row.props.children)
    .filter((c) => c.type === "th")
    .map((th) => textOf(th.props.children));
}

// The wrapper still scrolls on a wide screen: a card body is capped at 68ch
// and a six-column table can outrun it.
function MdTable({ children }) {
  const headers = useMemo(() => headersOf(children), [children]);
  return (
    <HeaderContext.Provider value={headers}>
      <div className="mk-table-wrap">
        <table>{children}</table>
      </div>
    </HeaderContext.Provider>
  );
}

function MdTr({ children }) {
  const headers = useContext(HeaderContext);
  return (
    <tr>
      {Children.map(children, (cell, i) =>
        cell?.type === "td" && headers[i]
          ? cloneElement(cell, { "data-label": headers[i] })
          : cell)}
    </tr>
  );
}

// Marco marks the fold in the body itself. An HTML comment because
// react-markdown drops it silently: if this splitting ever goes away the card
// still renders correctly, with the marker invisible.
const FOLD = /\n[ \t]*<!--\s*more\s*-->[ \t]*\n/;

export function splitAtFold(body) {
  const md = body || "";
  const i = md.search(FOLD);
  if (i === -1) return [md, ""];
  return [md.slice(0, i), md.slice(i).replace(FOLD, "")];
}

const md = (onOpenCapture) => ({
  a: mdLink(onOpenCapture), table: MdTable, tr: MdTr, strong: MdStrong,
});

export default function PlaybookCard({
  card, isAdmin, expanded, onToggle, onSave, onOpenCapture, matchedInBody = false,
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(card);
  const [saving, setSaving] = useState(false);
  // Lives here rather than in Playbook so it shares the editor's lifecycle:
  // it appears beside the field that failed to save and is gone the moment
  // the editor is reopened or abandoned.
  const [saveError, setSaveError] = useState("");
  const [unfolded, setUnfolded] = useState(false);
  const headRef = useRef(null);
  // Set only by a tap on this card's own header. The drawer's check path also
  // expands a card, and that one must not steal the scroll — it has its own
  // anchor to land on.
  const scrollOnExpand = useRef(false);

  const [above, below] = useMemo(() => splitAtFold(card.body_md), [card.body_md]);

  // Cards were single-open and did not scroll: tapping card 6 collapsed
  // whatever was open above it, the page shortened under the finger, and the
  // header just tapped ended up off screen.
  useEffect(() => {
    if (!expanded || !scrollOnExpand.current) return;
    scrollOnExpand.current = false;
    headRef.current?.scrollIntoView({
      block: "start",
      behavior: window.matchMedia("(prefers-reduced-motion: reduce)").matches
        ? "auto" : "smooth",
    });
  }, [expanded]);

  // A collapsed card reopens folded. The fold is a reading aid, not a setting.
  useEffect(() => { if (!expanded) setUnfolded(false); }, [expanded]);

  const toggle = () => {
    scrollOnExpand.current = !expanded;
    onToggle();
  };

  const startEdit = (e) => {
    e.stopPropagation();
    setDraft(card);
    setSaveError("");
    setEditing(true);
  };

  // Edit lives inside the header control below. Its own keydown must not
  // bubble to the header's Enter/Space handling, or activating Edit from
  // the keyboard would also toggle expand/collapse.
  const stopKeyPropagation = (e) => e.stopPropagation();

  const handleHeadKeyDown = (e) => {
    if (e.key === "Enter") {
      toggle();
    } else if (e.key === " " || e.key === "Spacebar") {
      // Prevent the page from scrolling on Space, then toggle.
      e.preventDefault();
      toggle();
    }
  };

  const save = async () => {
    setSaving(true);
    setSaveError("");
    try {
      await onSave(card.slug, {
        title: draft.title,
        summary: draft.summary,
        owner: draft.owner || "",
        body_md: draft.body_md,
        blocked: draft.blocked,
        blocked_reason: draft.blocked_reason || "",
      });
      setEditing(false);
    } catch (e) {
      // Staying in edit mode is the point: `draft` is still here, so an 11 KB
      // body is not retyped. Before this, the rejection escaped unhandled and
      // the only signal was that the editor happened not to close.
      setSaveError(formatApiErrorDetail(e?.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="mk-card" data-testid={`mk-card-${card.slug}`}>
      <div
        ref={headRef}
        className="mk-card-head"
        role="button"
        tabIndex={0}
        aria-expanded={expanded}
        onClick={toggle}
        onKeyDown={handleHeadKeyDown}
      >
        <div>
          <div className="mk-card-title">
            {card.title}
            {/* Which cards are mine, answered before the card is opened. */}
            {OWNER_LABEL[card.owner] && (
              <span className={`mk-owner owner-${card.owner}`}
                data-testid={`mk-owner-${card.slug}`}>{OWNER_LABEL[card.owner]}</span>
            )}
            {card.blocked && <span className="mk-blocked">Blocked</span>}
          </div>
          <div className="mk-card-summary">{card.summary}</div>
          <div className="mk-card-foot">
            {/* How long this is, before committing to opening it on a phone.
                One of these cards is six thousand characters. */}
            <span className="mk-read" data-testid={`mk-read-${card.slug}`}>
              {readMinutes(card)} min read
            </span>
            {matchedInBody && (
              <span className="mk-matched" data-testid={`mk-matched-${card.slug}`}>
                Matched inside — open to see it
              </span>
            )}
          </div>
        </div>
        <div className="mk-card-actions">
          {isAdmin && !editing && (
            <button type="button" className="mk-btn-quiet" onClick={startEdit}
              onKeyDown={stopKeyPropagation}>Edit</button>
          )}
          <span className="mk-chev">{expanded ? "▾" : "▸"}</span>
        </div>
      </div>

      {card.blocked && card.blocked_reason && expanded && (
        <div className="mk-blocked-note">{card.blocked_reason}</div>
      )}

      {expanded && !editing && (
        <div className="mk-card-body mk-md">
          <ReactMarkdown remarkPlugins={[remarkGfm]} components={md(onOpenCapture)}>
            {above}
          </ReactMarkdown>
          {below && !unfolded && (
            <button type="button" className="mk-btn-quiet mk-unfold"
              onClick={() => setUnfolded(true)}
              data-testid={`mk-unfold-${card.slug}`}>
              The detail ▾
            </button>
          )}
          {below && unfolded && (
            <ReactMarkdown remarkPlugins={[remarkGfm]} components={md(onOpenCapture)}>
              {below}
            </ReactMarkdown>
          )}
        </div>
      )}

      {expanded && editing && (
        <div className="mk-card-body">
          <label className="mk-label">Title</label>
          <input className="mk-input" value={draft.title}
            onChange={(e) => setDraft({ ...draft, title: e.target.value })} />
          <label className="mk-label">Summary</label>
          <input className="mk-input" value={draft.summary}
            onChange={(e) => setDraft({ ...draft, summary: e.target.value })} />
          <label className="mk-label">Who it is for</label>
          <select className="mk-select" value={draft.owner || ""}
            onChange={(e) => setDraft({ ...draft, owner: e.target.value })}>
            {OWNER_OPTIONS.map((o) => (
              <option key={o} value={o}>{OWNER_LABEL[o] || "Nobody — reference only"}</option>
            ))}
          </select>
          <label className="mk-label">Body (markdown)</label>
          <textarea className="mk-textarea" rows={20} value={draft.body_md}
            onChange={(e) => setDraft({ ...draft, body_md: e.target.value })} />
          <label className="mk-check">
            <input type="checkbox" checked={!!draft.blocked}
              onChange={(e) => setDraft({ ...draft, blocked: e.target.checked })} />
            Blocked — say why below
          </label>
          {draft.blocked && (
            <input className="mk-input" placeholder="Why is this blocked?"
              value={draft.blocked_reason || ""}
              onChange={(e) => setDraft({ ...draft, blocked_reason: e.target.value })} />
          )}
          {saveError && (
            <div className="mk-error" data-testid={`mk-card-error-${card.slug}`}>
              <div>Not saved. {saveError}</div>
              <div>Your edits are still here — try Save again.</div>
            </div>
          )}
          <div className="mk-row-end">
            <button type="button" className="mk-btn-quiet"
              onClick={() => { setSaveError(""); setEditing(false); }}>Cancel</button>
            <button type="button" className="mk-btn" disabled={saving} onClick={save}>
              {saving ? "Saving…" : "Save"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
